import React, { useState } from 'react';
import { SECTIONS } from '../utils/formConfig';

export default function AssessmentForm({ formData, onChange, onSubmit, isLoading }) {
  const [idx, setIdx] = useState(0);
  const section = SECTIONS[idx];
  const isLast = idx === SECTIONS.length - 1;

  const getValue = (key) => formData[key] || '';

  const handleChip = (key, val) => {
    onChange(key, getValue(key) === val ? '' : val);
  };

  const isSelected = (key, val) => getValue(key) === val;

  const sectionDone = section.fields.filter(f => f.required).every(f => {
    const v = formData[f.key];
    return v && v !== '';
  });

  const allFields = SECTIONS.reduce((a, s) => a + s.fields.filter(f => f.required).length, 0);
  const allFilled = SECTIONS.reduce((a, s) => a + s.fields.filter(f => f.required && formData[f.key]).length, 0);
  const overallPct = Math.round((allFilled / allFields) * 100);

  const secFields = section.fields.filter(f => f.required);
  const secFilled = secFields.filter(f => formData[f.key]).length;
  const secPct = secFields.length ? Math.round((secFilled / secFields.length) * 100) : 100;

  const next = () => {
    if (isLast) { onSubmit(); return; }
    setIdx(i => i + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prev = () => { setIdx(i => i - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(7,7,14,.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ height: 3, background: 'var(--surface)' }}>
          <div style={{ height: '100%', width: `${overallPct}%`, background: 'linear-gradient(90deg,var(--violet),var(--cyan))', transition: 'width .4s ease', borderRadius: '0 2px 2px 0' }} />
        </div>
        <div style={{ maxWidth: 840, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, padding: '0 28px' }}>
          <div style={{ fontFamily: 'var(--ff-display)', fontSize: 22, fontWeight: 800, letterSpacing: '-0.04em', background: 'linear-gradient(135deg,#f0f0ff,#a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            SentinelAI
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            {SECTIONS.map((s, i) => (
              <div key={i} style={{
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: 'var(--ff-mono)', fontSize: 11, transition: 'all .2s',
                background: i < idx ? 'rgba(16,185,129,.2)' : i === idx ? 'rgba(124,58,237,.25)' : 'var(--surface)',
                border: `1px solid ${i < idx ? 'rgba(16,185,129,.4)' : i === idx ? 'rgba(124,58,237,.5)' : 'var(--border)'}`,
                color: i < idx ? 'var(--emerald)' : i === idx ? 'var(--violetbr)' : 'var(--txt3)'
              }}>
                {i < idx ? '✓' : i + 1}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main */}
      <div key={idx} style={{ flex: 1, maxWidth: 840, margin: '0 auto', width: '100%', padding: '44px 28px 120px' }} className="fadeup">
        {/* Section header */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--violetbr)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            STEP 1 · SECTION {idx + 1}/{SECTIONS.length}
          </div>
          <h1 style={{ fontFamily: 'var(--ff-display)', fontSize: 'clamp(28px,5vw,44px)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1.1, marginBottom: 6 }}>
            Estimate Your <span style={{ color: 'var(--violetbr)' }}>Risk</span>
          </h1>
          <p style={{ fontSize: 15, color: 'var(--txt2)' }}>{section.icon} {section.title} — {section.subtitle}</p>
        </div>

        {/* Section progress */}
        <div style={{ marginBottom: 36 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--txt2)' }}>{section.title} Progress</span>
            <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--violetbr)' }}>{secPct}% complete</span>
          </div>
          <div style={{ height: 4, background: 'var(--surface)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${secPct}%`, background: 'linear-gradient(90deg,var(--violet),var(--cyan))', transition: 'width .4s ease', borderRadius: 2 }} />
          </div>
        </div>

        {/* Fields */}
        {section.fields.map(field => (
          <div key={field.key} style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: '24px 28px', marginBottom: 14, transition: 'border-color .2s' }}>
            <label style={{ display: 'block', fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--txt2)', letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 14 }}>
              {field.label}
              {field.required && <span style={{ color: 'var(--rose)', marginLeft: 3 }}>*</span>}
            </label>

            {field.type === 'text' || field.type === 'email' ? (
              <input
                type={field.type}
                placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                value={getValue(field.key)}
                onChange={e => onChange(field.key, e.target.value)}
                style={{ width: '100%', background: 'var(--surface)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 10, padding: '12px 14px', fontSize: 15, color: 'var(--txt)', outline: 'none' }}
                onFocus={e => { e.target.style.borderColor = 'var(--violetbr)'; e.target.style.boxShadow = '0 0 0 3px rgba(124,58,237,.12)'; }}
                onBlur={e => { e.target.style.borderColor = 'rgba(255,255,255,.1)'; e.target.style.boxShadow = 'none'; }}
              />
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {field.options.map(opt => {
                  const sel = isSelected(field.key, opt);
                  return (
                    <button key={opt} onClick={() => handleChip(field.key, opt)} type="button"
                      style={{
                        padding: '8px 16px', borderRadius: 100, fontSize: 13, transition: 'all .15s',
                        border: `1.5px solid ${sel ? 'var(--violetbr)' : 'rgba(255,255,255,.1)'}`,
                        background: sel ? 'rgba(124,58,237,.2)' : 'var(--surface)',
                        color: sel ? 'var(--txt)' : 'var(--txt2)',
                        boxShadow: sel ? '0 0 12px rgba(124,58,237,.2)' : 'none'
                      }}
                      onMouseEnter={e => { if (!sel) { e.currentTarget.style.borderColor = 'rgba(124,58,237,.4)'; e.currentTarget.style.color = 'var(--txt)'; } }}
                      onMouseLeave={e => { if (!sel) { e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'; e.currentTarget.style.color = 'var(--txt2)'; } }}>
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer nav */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(7,7,14,.92)', backdropFilter: 'blur(20px)', borderTop: '1px solid var(--border)', padding: '14px 28px', zIndex: 100 }}>
        <div style={{ maxWidth: 840, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--txt3)' }}>
              Section <span style={{ color: 'var(--txt2)' }}>{idx + 1}</span> of <span style={{ color: 'var(--txt2)' }}>{SECTIONS.length}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
              <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--txt3)', whiteSpace: 'nowrap' }}>Overall Progress</span>
              <div style={{ width: 120, height: 3, background: 'var(--surface)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${overallPct}%`, background: 'linear-gradient(90deg,var(--violet),var(--cyan))', transition: 'width .4s ease' }} />
              </div>
              <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 10, color: 'var(--violetbr)' }}>{overallPct}%</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {idx > 0 && (
              <button onClick={prev} disabled={isLoading}
                style={{ padding: '11px 24px', borderRadius: 10, background: 'transparent', border: '1px solid rgba(255,255,255,.12)', color: 'var(--txt2)', fontSize: 14, fontWeight: 600, transition: 'all .2s' }}
                onMouseEnter={e => { e.target.style.borderColor = 'var(--borderacc)'; }}
                onMouseLeave={e => { e.target.style.borderColor = 'rgba(255,255,255,.12)'; }}>
                ← Previous
              </button>
            )}
            <button onClick={next} disabled={!sectionDone || isLoading}
              style={{
                padding: '11px 28px', borderRadius: 10, border: 'none', fontFamily: 'var(--ff-display)',
                fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em', transition: 'all .2s',
                background: sectionDone ? 'linear-gradient(135deg,var(--violet),#6d28d9)' : 'var(--surface)',
                color: '#fff', opacity: sectionDone ? 1 : .45,
                boxShadow: sectionDone ? '0 0 22px rgba(124,58,237,.4)' : 'none'
              }}>
              {isLoading ? 'Analysing...' : isLast ? 'Calculate Risk ⚡' : 'Next Section →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
